import {
  AppstoreOutlined,
  CrownOutlined,
  DeleteOutlined,
  PlusOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined,
  SaveOutlined,
  ShoppingCartOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { Button, Input, Popconfirm, Select } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useGlobalLoader } from '@/hooks/useGlobalLoader';
import { useNotify } from '@/hooks/useNotify';
import {
  useCreateAccessProfileMutation,
  useDeleteAccessProfileMutation,
  useGetPermissionsConfigQuery,
  useUpdateAccessProfileMutation,
} from '@/modules/users/usersApi';
import type { AccessProfile } from '@/types/adminUser';
import { resolveMutationResult } from '@/utils/apiResponse';
import { ModuleAccessPicker } from './ModuleAccessPicker';
import styles from './RolesPermissionsTab.module.scss';

type SelectedTarget =
  | { type: 'super_admin' }
  | { type: 'profile'; profileId: string; isNew?: boolean };

const BUILTIN_ROLE_IDS = new Set(['role_sub_admin', 'role_product_manager', 'role_sales_manager']);

function profileIcon(name: string) {
  const lower = name.toLowerCase();
  if (lower.includes('product')) return AppstoreOutlined;
  if (lower.includes('sales')) return ShoppingCartOutlined;
  if (lower.includes('sub') || lower.includes('admin')) return SafetyCertificateOutlined;
  return TeamOutlined;
}

export function RolesPermissionsTab() {
  const notify = useNotify();
  const { data: config, isLoading, isFetching } = useGetPermissionsConfigQuery();
  const [createAccessProfile, { isLoading: isCreating }] = useCreateAccessProfileMutation();
  const [updateAccessProfile, { isLoading: isUpdatingProfile }] = useUpdateAccessProfileMutation();
  const [deleteAccessProfile, { isLoading: isDeleting }] = useDeleteAccessProfileMutation();

  const [selected, setSelected] = useState<SelectedTarget>({ type: 'profile', profileId: 'role_sub_admin' });
  const [draftModules, setDraftModules] = useState<string[]>([]);
  const [draftName, setDraftName] = useState('');
  const [draftBaseRole, setDraftBaseRole] = useState<'admin' | 'manager'>('manager');

  useGlobalLoader((isLoading && !config) || isFetching);

  const teamRoles = useMemo(() => config?.customProfiles ?? [], [config?.customProfiles]);
  const isSaving = isCreating || isUpdatingProfile || isDeleting;

  const selectedProfile = useMemo(() => {
    if (selected.type !== 'profile' || selected.isNew) return null;
    return teamRoles.find((profile) => profile.id === selected.profileId) ?? null;
  }, [selected, teamRoles]);

  const selectedKey =
    selected.type === 'super_admin'
      ? 'super_admin'
      : selected.isNew
        ? 'new'
        : selected.profileId;

  useEffect(() => {
    if (selected.type === 'super_admin') {
      setDraftModules([]);
      setDraftName('');
      return;
    }

    if (selected.isNew) {
      setDraftModules(['dashboard', 'orders', 'customers']);
      setDraftName('');
      setDraftBaseRole('manager');
      return;
    }

    const profile = teamRoles.find((item) => item.id === selected.profileId);
    if (!profile) return;

    setDraftModules(profile.modules);
    setDraftName(profile.name);
    setDraftBaseRole(profile.baseRole);
  }, [selectedKey, teamRoles, selected]);

  useEffect(() => {
    if (!teamRoles.length || selectedKey === 'super_admin' || selectedKey === 'new') return;
    const exists = teamRoles.some((profile) => profile.id === selectedKey);
    if (!exists) {
      setSelected({ type: 'profile', profileId: teamRoles[0].id });
    }
  }, [selectedKey, teamRoles]);

  const isDirty = useMemo(() => {
    if (selected.type === 'super_admin') return false;

    if (selected.isNew) {
      return draftName.trim().length > 0;
    }

    if (!selectedProfile) return false;
    if (selectedProfile.name !== draftName.trim()) return true;
    if (selectedProfile.baseRole !== draftBaseRole) return true;
    if (selectedProfile.modules.length !== draftModules.length) return true;
    return selectedProfile.modules.some((key) => !draftModules.includes(key));
  }, [draftBaseRole, draftModules, draftName, selected, selectedProfile]);

  const handleReset = () => {
    if (selected.type === 'super_admin') return;

    if (selected.isNew) {
      setSelected({ type: 'profile', profileId: teamRoles[0]?.id ?? 'role_sub_admin' });
      return;
    }

    if (!selectedProfile) return;
    setDraftModules(selectedProfile.modules);
    setDraftName(selectedProfile.name);
    setDraftBaseRole(selectedProfile.baseRole);
  };

  const handleSave = async () => {
    if (selected.type === 'super_admin') return;

    if (!draftName.trim()) {
      notify.error('Please enter a role name.');
      return;
    }

    if (selected.isNew) {
      const result = await resolveMutationResult<AccessProfile>(
        createAccessProfile({
          name: draftName.trim(),
          baseRole: draftBaseRole,
          modules: draftModules,
        }),
      );

      if (result.ok) {
        notify.success(result.message ?? 'Role created.');
        setSelected({ type: 'profile', profileId: result.data.id });
        return;
      }
      notify.error(result.message);
      return;
    }

    const result = await resolveMutationResult<AccessProfile>(
      updateAccessProfile({
        id: selected.profileId,
        body: {
          name: draftName.trim(),
          baseRole: draftBaseRole,
          modules: draftModules,
        },
      }),
    );

    if (result.ok) {
      notify.success(result.message ?? 'Role updated.');
      return;
    }
    notify.error(result.message);
  };

  const handleDeleteProfile = async (profileId: string) => {
    const result = await resolveMutationResult(deleteAccessProfile(profileId));
    if (result.ok) {
      notify.success(result.message ?? 'Role deleted.');
      setSelected({ type: 'profile', profileId: 'role_sub_admin' });
      return;
    }
    notify.error(result.message);
  };

  const editorTitle =
    selected.type === 'super_admin'
      ? 'Super Admin access'
      : selected.isNew
        ? 'New team role'
        : `${draftName || 'Team role'} access`;

  const isBuiltinRole = Boolean(selectedProfile && BUILTIN_ROLE_IDS.has(selectedProfile.id));
  const showRoleFields =
    selected.type === 'profile' && (Boolean(selected.isNew) || !isBuiltinRole);

  return (
    <div className={styles.wrap}>
      <div className={styles.header}>
        <div>
          <h2 className={styles.title}>Roles &amp; permissions</h2>
          <p className={styles.desc}>
            Configure team roles like Sub Admin, Product Manager, and Sales Manager. Assign these
            when creating staff accounts.
          </p>
        </div>
        {selected.type !== 'super_admin' ? (
          <div className={styles.headerActions}>
            <Button icon={<ReloadOutlined />} disabled={!isDirty || isSaving} onClick={handleReset}>
              Reset
            </Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={isSaving}
              disabled={!isDirty}
              onClick={() => void handleSave()}
            >
              {selected.isNew ? 'Create role' : 'Save role'}
            </Button>
          </div>
        ) : null}
      </div>

      <div className={styles.sectionLabel}>System role</div>
      <div className={styles.roleTabs}>
        <button
          type="button"
          className={`${styles.roleTab} ${styles.roleTab_super_admin} ${selected.type === 'super_admin' ? styles.roleTabActive : ''}`}
          onClick={() => setSelected({ type: 'super_admin' })}
        >
          <span className={styles.roleTabIcon}>
            <CrownOutlined />
          </span>
          <span className={styles.roleTabLabel}>Super Admin</span>
        </button>
      </div>

      <div className={styles.sectionHeadRow}>
        <div className={styles.sectionLabel}>Team roles</div>
        <Button type="dashed" icon={<PlusOutlined />} onClick={() => setSelected({ type: 'profile', profileId: 'new', isNew: true })}>
          Add role
        </Button>
      </div>

      <div className={styles.roleTabs}>
        {teamRoles.map((profile) => {
          const Icon = profileIcon(profile.name);
          const active =
            selected.type === 'profile' && !selected.isNew && selected.profileId === profile.id;

          return (
            <button
              key={profile.id}
              type="button"
              className={`${styles.roleTab} ${styles.roleTabCustom} ${active ? styles.roleTabActive : ''}`}
              onClick={() => setSelected({ type: 'profile', profileId: profile.id })}
            >
              <span className={styles.roleTabIcon}>
                <Icon />
              </span>
              <span className={styles.roleTabLabel}>{profile.name}</span>
              <span className={styles.profileCount}>{profile.modules.length}</span>
            </button>
          );
        })}
      </div>

      <div className={styles.editorCard}>
        <div className={styles.editorHead}>
          <div>
            <h3 className={styles.editorTitle}>{editorTitle}</h3>
            <p className={styles.editorDesc}>
              {selected.type === 'super_admin'
                ? 'Full unrestricted access to every module.'
                : 'Choose which dashboard modules this role can access.'}
            </p>
          </div>
          {selected.type !== 'super_admin' ? (
            <span className={styles.moduleCount}>{draftModules.length} modules selected</span>
          ) : null}
        </div>

        {selected.type === 'super_admin' ? (
          <div className={styles.superAdminNotice}>
            <CrownOutlined />
            <div>
              <strong>Unrestricted access</strong>
              <p>Super admins always have access to all modules, including user management and settings.</p>
            </div>
          </div>
        ) : (
          <>
            {showRoleFields ? (
              <div className={styles.roleMeta}>
                <div className={styles.roleMetaField}>
                  <label className={styles.fieldLabel} htmlFor="profile-name">
                    Role name
                  </label>
                  <Input
                    id="profile-name"
                    size="large"
                    placeholder="e.g. Warehouse Manager"
                    value={draftName}
                    onChange={(event) => setDraftName(event.target.value)}
                  />
                </div>

                <div className={styles.roleMetaField}>
                  <label className={styles.fieldLabel} htmlFor="base-role">
                    Access type
                  </label>
                  <Select
                    id="base-role"
                    size="large"
                    className={styles.baseRoleSelect}
                    value={draftBaseRole}
                    onChange={(value) => setDraftBaseRole(value)}
                    options={[
                      { value: 'admin', label: 'Admin' },
                      { value: 'manager', label: 'Manager' },
                    ]}
                  />
                </div>

                {!selected.isNew ? (
                  <div className={styles.roleMetaActions}>
                    <Popconfirm
                      title="Delete this role?"
                      description="Users with this role will fall back to default access."
                      onConfirm={() => void handleDeleteProfile(selected.profileId)}
                    >
                      <Button danger icon={<DeleteOutlined />} loading={isDeleting}>
                        Delete
                      </Button>
                    </Popconfirm>
                  </div>
                ) : null}
              </div>
            ) : (
              <div className={styles.builtinRoleHint}>
                <SafetyCertificateOutlined />
                <span>
                  Built-in role — adjust module access below. Assign this role when creating staff
                  accounts.
                </span>
              </div>
            )}

            <ModuleAccessPicker value={draftModules} onChange={setDraftModules} />
          </>
        )}
      </div>
    </div>
  );
}
